import React, { useState } from 'react';
import './AgreementCreator.css';
import { toast } from 'react-toastify';

const AgreementCreator = ({ web3, account, onSuccess }) => {
  const [formData, setFormData] = useState({
    buyerAddress: '',
    sellerAddress: '',
    considerationAmount: '',
    releaseDate: '',
    mediatorAddress: '',
    cancelationBlocks: '3600', // Default to 3600 blocks (about 1 day on Polygon)
    agreementTitle: '',
    agreementType: 'sale',
    customAgreement: false
  });

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatedTemplate, setGeneratedTemplate] = useState('');

  // California-compliant agreement templates
  const templates = {
    sale: `
# PURCHASE AGREEMENT (CALIFORNIA)

## PARTIES

Buyer: [BUYER_ADDRESS]
Seller: [SELLER_ADDRESS]
Mediator: [MEDIATOR_ADDRESS]

## AGREEMENT DETAILS

Title: [AGREEMENT_TITLE]
Consideration Amount: [CONSIDERATION_AMOUNT] MATIC
Release Date: [RELEASE_DATE]
Cancellation Period: [CANCELLATION_BLOCKS] blocks

## TERMS AND CONDITIONS

This agreement ("Agreement") is entered into between the Buyer and Seller identified above, in accordance with California Civil Code §1550-1552, governing the essential elements of a contract.

### 1. SUBJECT MATTER
The Seller agrees to transfer ownership of the specified item or service to the Buyer in exchange for the Consideration Amount.

### 2. PAYMENT
The Buyer agrees to pay the Consideration Amount through this smart contract, to be held in escrow until the conditions for release are met.

### 3. PERFORMANCE
The Seller agrees to perform all obligations under this Agreement in good faith and in a commercially reasonable manner.

### 4. ELECTRONIC CONSENT
Pursuant to California Civil Code §1633.7 (Uniform Electronic Transactions Act), the parties acknowledge and agree that:
(a) This Agreement may be executed using electronic signatures, which shall have the same legal effect as handwritten signatures.
(b) The blockchain records of this Agreement shall be considered an "electronic record" as defined by law.
(c) Both parties expressly consent to conduct this transaction by electronic means.

### 5. DISPUTE RESOLUTION
(a) Any dispute arising out of or related to this Agreement shall be subject to mediation by the designated Mediator, in accordance with California Code of Civil Procedure §1281-1281.96.
(b) The Mediator's decision will be binding and automatically enforced through the smart contract.
(c) Mediation costs shall be equally shared by both parties unless otherwise determined by the Mediator.

### 6. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of the State of California, without giving effect to any choice of law or conflict of law provisions.

### 7. SEVERABILITY
If any provision of this Agreement is found to be invalid or unenforceable, the remaining provisions will remain in full force and effect.

### 8. ENTIRE AGREEMENT
This Agreement, including any attachments, constitutes the entire agreement between the parties with respect to the subject matter hereof.

## EXECUTION
By interacting with the smart contract, the parties agree to be bound by the terms and conditions of this Agreement in accordance with California law.
`,
    service: `
# SERVICE AGREEMENT (CALIFORNIA)

## PARTIES

Client: [BUYER_ADDRESS]
Service Provider: [SELLER_ADDRESS]
Mediator: [MEDIATOR_ADDRESS]

## AGREEMENT DETAILS

Title: [AGREEMENT_TITLE]
Service Fee: [CONSIDERATION_AMOUNT] MATIC
Completion Date: [RELEASE_DATE]
Cancellation Period: [CANCELLATION_BLOCKS] blocks

## TERMS AND CONDITIONS

This service agreement ("Agreement") is entered into between the Client and Service Provider identified above, in accordance with California Civil Code §1549-1701, governing contracts for services.

### 1. SERVICES
The Service Provider agrees to provide the services described in the Agreement Title to the Client in a professional and workmanlike manner.

### 2. COMPENSATION
The Client agrees to pay the Service Fee through this smart contract, to be held in escrow until the services are satisfactorily completed.

### 3. TERM AND TERMINATION
This Agreement commences upon mutual consent through the smart contract and continues until the Completion Date or until terminated in accordance with this Agreement.

### 4. ELECTRONIC EXECUTION
Pursuant to California Civil Code §1633.7 (Uniform Electronic Transactions Act), the parties acknowledge and agree that:
(a) This Agreement may be executed using blockchain wallet signatures, which shall have the same legal effect as handwritten signatures.
(b) The smart contract and blockchain records shall be considered valid "electronic records" as defined by law.
(c) Both parties expressly consent to using electronic means for this transaction.

### 5. DISPUTE RESOLUTION
(a) Any dispute arising out of this Agreement shall be submitted to mediation by the designated Mediator in compliance with California Code of Civil Procedure §1281-1281.96.
(b) The Mediator's decision will be binding and automatically executed through the smart contract.
(c) Mediation fees shall be equally shared unless otherwise determined by the Mediator.

### 6. INDEPENDENT CONTRACTOR STATUS
The Service Provider is an independent contractor, not an employee of the Client. The Service Provider is responsible for all taxes and business expenses.

### 7. LIMITATION OF LIABILITY
Except as otherwise prohibited by California law, neither party shall be liable for indirect, special, or consequential damages arising from this Agreement.

### 8. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with the laws of the State of California, including California Business and Professions Code where applicable.

### 9. ENTIRE AGREEMENT
This Agreement constitutes the entire understanding between the parties with respect to the subject matter hereof.

## EXECUTION
By interacting with the smart contract, the parties agree to be bound by the terms and conditions of this Agreement in accordance with California law.
`,
    lease: `
# PROPERTY LEASE AGREEMENT (CALIFORNIA)

## PARTIES

Tenant: [BUYER_ADDRESS]
Landlord: [SELLER_ADDRESS]
Mediator: [MEDIATOR_ADDRESS]

## AGREEMENT DETAILS

Property Description: [AGREEMENT_TITLE]
Lease Amount: [CONSIDERATION_AMOUNT] MATIC
Lease Expiration: [RELEASE_DATE]
Termination Notice Period: [CANCELLATION_BLOCKS] blocks

## TERMS AND CONDITIONS

This lease agreement ("Agreement") is entered into between the Landlord and Tenant identified above, in accordance with California Civil Code §1940-1954, which governs landlord-tenant relationships.

### 1. PREMISES
The Landlord agrees to lease to Tenant the property described above.

### 2. RENT
The Tenant agrees to pay the Lease Amount specified above through this smart contract.

### 3. TERM
This lease shall commence upon mutual consent through the smart contract and continue until the Lease Expiration date.

### 4. ELECTRONIC TRANSACTION
Pursuant to California Civil Code §1633 (Uniform Electronic Transactions Act):
(a) This Agreement may be executed using electronic signatures via blockchain wallet authentication.
(b) Both parties consent to conducting this transaction by electronic means.
(c) The blockchain record shall serve as a valid electronic record of this transaction.

### 5. SECURITY DEPOSIT
Any security deposit shall be handled in accordance with California Civil Code §1950.5.

### 6. MAINTENANCE AND REPAIRS
The Landlord shall maintain the premises in a habitable condition as required by California Civil Code §1941.1.

### 7. ENTRY BY LANDLORD
The Landlord may enter the premises only under circumstances permitted by California Civil Code §1954.

### 8. DISPUTE RESOLUTION
(a) Any dispute shall be subject to mediation by the designated Mediator in accordance with California law.
(b) The mediation process shall comply with California Code of Civil Procedure §1281-1281.96.
(c) The Mediator's decision will be binding and automatically enforced through the smart contract.

### 9. GOVERNING LAW
This Agreement shall be governed by and construed in accordance with California law, including all applicable landlord-tenant statutes and regulations.

### 10. SEVERABILITY
If any provision of this Agreement is found to be invalid, the remaining provisions shall remain in effect.

## EXECUTION
By interacting with the smart contract, the parties agree to be bound by the terms of this Agreement in accordance with California law.
`
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // If agreement type changes, reset custom agreement flag
    if (name === 'agreementType') {
      setFormData(prev => ({
        ...prev,
        customAgreement: false
      }));
    }
  };

  const nextStep = () => {
    if (step === 1) {
      // Validate first step
      if (!formData.buyerAddress || !formData.sellerAddress || !formData.mediatorAddress) {
        toast.error('Please fill in all addresses');
        return;
      }
      if (!web3.utils.isAddress(formData.buyerAddress) || 
          !web3.utils.isAddress(formData.sellerAddress) ||
          !web3.utils.isAddress(formData.mediatorAddress)) {
        toast.error('Please enter valid Ethereum addresses');
        return;
      }
    }

    if (step === 2) {
      // Validate second step
      if (!formData.considerationAmount || !formData.releaseDate || !formData.agreementTitle) {
        toast.error('Please fill in all agreement details');
        return;
      }

      // Generate template from selected type
      let template = templates[formData.agreementType] || templates.sale;
      
      // Replace placeholders with actual values
      template = template
        .replace('[BUYER_ADDRESS]', formData.buyerAddress)
        .replace('[SELLER_ADDRESS]', formData.sellerAddress)
        .replace('[MEDIATOR_ADDRESS]', formData.mediatorAddress)
        .replace('[AGREEMENT_TITLE]', formData.agreementTitle)
        .replace('[CONSIDERATION_AMOUNT]', formData.considerationAmount)
        .replace('[RELEASE_DATE]', new Date(formData.releaseDate).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        }))
        .replace('[CANCELLATION_BLOCKS]', formData.cancelationBlocks);
        
      setGeneratedTemplate(template);
    }

    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Code to deploy contract would go here
      // For now, we'll just simulate success
      toast.success('Agreement created successfully!');
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error creating agreement:', error);
      toast.error('Failed to create agreement. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Conditional rendering based on step
  const renderStep = () => {
    switch(step) {
      case 1:
        return (
          <div className="agreement-step">
            <h2>Step 1: Party Information</h2>
            <div className="form-group">
              <label>Buyer Address</label>
              <input
                type="text"
                name="buyerAddress"
                value={formData.buyerAddress}
                onChange={handleChange}
                placeholder="0x..."
                className="form-control"
              />
              {account && (
                <button
                  type="button"
                  className="use-wallet-btn"
                  onClick={() => setFormData({...formData, buyerAddress: account})}
                >
                  Use My Wallet
                </button>
              )}
            </div>

            <div className="form-group">
              <label>Seller Address</label>
              <input
                type="text"
                name="sellerAddress"
                value={formData.sellerAddress}
                onChange={handleChange}
                placeholder="0x..."
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Mediator Address</label>
              <input
                type="text"
                name="mediatorAddress"
                value={formData.mediatorAddress}
                onChange={handleChange}
                placeholder="0x..."
                className="form-control"
              />
              <span className="field-help">
                The mediator will resolve disputes according to California Code of Civil Procedure §1281.
              </span>
            </div>

            <div className="step-buttons">
              <button
                type="button"
                className="next-btn"
                onClick={nextStep}
              >
                Next
              </button>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="agreement-step">
            <h2>Step 2: Agreement Details</h2>
            
            <div className="form-group">
              <label>Agreement Title</label>
              <input
                type="text"
                name="agreementTitle"
                value={formData.agreementTitle}
                onChange={handleChange}
                placeholder="e.g., Purchase of Used Bicycle"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Agreement Type</label>
              <select
                name="agreementType"
                value={formData.agreementType}
                onChange={handleChange}
                className="form-control"
              >
                <option value="sale">Sale of Goods</option>
                <option value="service">Service Agreement</option>
                <option value="lease">Property Lease</option>
              </select>
              <span className="field-help">
                Select a California-compliant agreement template.
              </span>
            </div>

            <div className="form-group">
              <label>Consideration Amount (MATIC)</label>
              <input
                type="number"
                name="considerationAmount"
                value={formData.considerationAmount}
                onChange={handleChange}
                placeholder="0.001"
                step="0.001"
                min="0"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label>Release Date</label>
              <input
                type="date"
                name="releaseDate"
                value={formData.releaseDate}
                onChange={handleChange}
                className="form-control"
                min={new Date().toISOString().split('T')[0]}
              />
              <span className="field-help">
                When the agreement should complete (funds released).
              </span>
            </div>

            <div className="form-group">
              <label>Cancellation Period (Blocks)</label>
              <input
                type="number"
                name="cancelationBlocks"
                value={formData.cancelationBlocks}
                onChange={handleChange}
                placeholder="3600"
                min="1"
                className="form-control"
              />
              <span className="field-help">
                Number of blocks during which cancellation is possible (3600 ≈ 1 day on Polygon).
              </span>
            </div>

            <div className="step-buttons">
              <button type="button" className="prev-btn" onClick={prevStep}>
                Back
              </button>
              <button type="button" className="next-btn" onClick={nextStep}>
                Next
              </button>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="agreement-step">
            <h2>Step 3: Review Agreement</h2>
            
            <div className="ca-law-note">
              <strong>California Law Compliance:</strong> This agreement is designed to comply with 
              California Civil Code §1550-1552 and the Uniform Electronic Transactions Act (Civil Code §1633).
            </div>

            <div className="template-preview">
              <div className="template-controls">
                <h3>Agreement Preview</h3>
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    name="customAgreement"
                    checked={formData.customAgreement}
                    onChange={handleChange}
                  />
                  <span>Customize Text</span>
                </label>
              </div>
              
              {formData.customAgreement ? (
                <textarea
                  className="template-editor"
                  value={generatedTemplate}
                  onChange={(e) => setGeneratedTemplate(e.target.value)}
                  rows={15}
                />
              ) : (
                <div className="template-display">
                  {generatedTemplate.split('\n').map((line, index) => {
                    if (line.startsWith('# ')) {
                      return <h1 key={index}>{line.substring(2)}</h1>;
                    } else if (line.startsWith('## ')) {
                      return <h2 key={index}>{line.substring(3)}</h2>;
                    } else if (line.startsWith('### ')) {
                      return <h3 key={index}>{line.substring(4)}</h3>;
                    } else if (line.trim() === '') {
                      return <br key={index} />;
                    } else {
                      return <p key={index}>{line}</p>;
                    }
                  })}
                </div>
              )}
            </div>

            <div className="step-buttons">
              <button type="button" className="prev-btn" onClick={prevStep}>
                Back
              </button>
              <button 
                type="button" 
                className="submit-btn" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Creating Agreement...' : 'Create Agreement'}
              </button>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="agreement-creator">
      <div className="progress-bar">
        <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Parties</div>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Details</div>
        </div>
        <div className="progress-line"></div>
        <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Review</div>
        </div>
      </div>
      
      <form className="agreement-form">
        {renderStep()}
      </form>
      
      <div className="california-compliance">
        <div className="compliance-icon">⚖️</div>
        <div className="compliance-text">
          <strong>California Legal Compliance:</strong> Agreements created on this platform comply with 
          California Civil Code §1550-1552 (essential elements of a contract), 
          §1619-1633 (express contracts and UETA), and Code of Civil Procedure §1281-1281.96 (arbitration).
        </div>
      </div>
    </div>
  );
};

export default AgreementCreator;